/**
 * Main Application Component
 *
 * Handles routing and navigation for the survey platform.
 * Uses sidebar layout for authenticated pages, minimal top nav for public pages.
 */

import React from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from 'react-router-dom'
import Home from './Home.jsx'
import Login from './Login.jsx'
import SurveyBuilder from './SurveyBuilder.jsx'
import Dashboard from './Dashboard.jsx'
import Analytics from './Analytics.jsx'
import RunSurvey from './RunSurvey.jsx'
import OrganizationSettings from './OrganizationSettings.jsx'
import SurveySettings from './SurveySettings.jsx'
import SurveyEditor from './SurveyEditor.jsx'
import FieldAgentManagement from './FieldAgentManagement.jsx'
import LocationAlerts from './LocationAlerts.jsx'
import Pricing from './Pricing.jsx'
import OrgSetup from './OrgSetup.jsx'
import OrgSignup from './OrgSignup.jsx'
import ShortUrlRedirect from './ShortUrlRedirect.jsx'
import Sidebar from './components/Sidebar/Sidebar.jsx'
import { AuthProvider } from './AuthContext.jsx'
import { useAuth } from './auth-context'
import './App.css'

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner-ring"></div>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/" replace />
  }

  return children
}

/**
 * Minimal public navigation bar for landing, login, pricing, and survey pages
 */
function PublicNavigation() {
  const { user } = useAuth()
  const location = useLocation()

  if (location.pathname.startsWith('/run/')) {
    return (
      <nav className="navbar">
        <span className="logo">SurveyTip</span>
        <div className="nav-links"></div>
      </nav>
    )
  }

  return (
    <nav className="navbar">
      <Link to="/" className="logo">
        SurveyTip
      </Link>
      <div className="nav-links">
        {user ? (
          <Link to="/dashboard" className="login-link">
            Dashboard
          </Link>
        ) : (
          <>
            <Link to="/pricing">Pricing</Link>
            <Link to="/login" className="login-link">
              Login
            </Link>
          </>
        )}
      </div>
    </nav>
  )
}

/**
 * Layout wrapper that decides between sidebar (auth) and public (no sidebar) layout
 */
function AppLayout() {
  const { user, loading } = useAuth()
  const location = useLocation()

  const publicPaths = ['/', '/login', '/signup', '/pricing', '/org-signup']
  const isRunSurvey = location.pathname.startsWith('/run/')
  const isShortUrl = location.pathname.startsWith('/s/')
  const isPublicPage = publicPaths.includes(location.pathname) || isRunSurvey || isShortUrl

  const showSidebar = user && !loading && !isPublicPage

  if (showSidebar) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="app-content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/surveybuilder"
              element={
                <ProtectedRoute>
                  <SurveyBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization"
              element={
                <ProtectedRoute>
                  <OrganizationSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/:id?"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey/:id/settings"
              element={
                <ProtectedRoute>
                  <SurveySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey/:id/edit"
              element={
                <ProtectedRoute>
                  <SurveyEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/survey/:id/field-agents"
              element={
                <ProtectedRoute>
                  <FieldAgentManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <LocationAlerts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/org-setup"
              element={
                <ProtectedRoute>
                  <OrgSetup />
                </ProtectedRoute>
              }
            />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/run/:id" element={<RunSurvey />} />
            <Route path="/s/:code" element={<ShortUrlRedirect />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    )
  }

  return (
    <div className="app-public">
      <header className="app-header">
        <PublicNavigation />
      </header>
      <div className="app-content-public">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/org-signup" element={<OrgSignup />} />
          <Route path="/run/:id" element={<RunSurvey />} />
          <Route path="/s/:code" element={<ShortUrlRedirect />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/surveybuilder"
            element={
              <ProtectedRoute>
                <SurveyBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization"
            element={
              <ProtectedRoute>
                <OrganizationSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics/:id?"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/survey/:id/settings"
            element={
              <ProtectedRoute>
                <SurveySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/survey/:id/edit"
            element={
              <ProtectedRoute>
                <SurveyEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/survey/:id/field-agents"
            element={
              <ProtectedRoute>
                <FieldAgentManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <LocationAlerts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-setup"
            element={
              <ProtectedRoute>
                <OrgSetup />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  )
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppLayout />
      </AuthProvider>
    </Router>
  )
}

export default App
